import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils.js";

const buttonVariants = cva(
  // `border border-transparent` in the base, not just on `outline`: a toggle
  // that swaps outline <-> secondary would otherwise resize by 2px on every
  // press, because only one of the two reserved space for a border. Every
  // variant now occupies the same box and `outline` only recolors it.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-transparent text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          // `bg-background` is opaque, so the text must be pinned to its
          // pairing `text-foreground` — without it the button inherits the
          // surrounding color and turns white-on-white on a colored surface
          // (e.g. Retry inside the marine message bubble in light theme).
          "border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        // Touch-first sizing: ≥44px on phones, compact at sm+ to keep the
        // existing dense desktop feel.
        default: "h-11 px-4 py-2 sm:h-9",
        sm: "h-10 rounded-md px-3 text-xs sm:h-8",
        // Toolbar controls must line up with Input, Select and
        // SegmentedControl, all of which are 44/36. `sm` is 40/32, so every
        // toolbar that wanted to line up was hand-patching it with
        // `className="h-11 sm:h-9"` — five call sites had already done so.
        // This is that patch, named. Keeps `sm`'s dense padding and type.
        toolbar: "h-11 rounded-md px-3 text-xs sm:h-9",
        lg: "h-12 rounded-md px-6 sm:h-10",
        icon: "h-11 w-11 sm:h-9 sm:w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingLabel?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      loadingLabel = "Working…",
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        aria-busy={loading || undefined}
        disabled={asChild ? undefined : disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <span
              aria-hidden="true"
              className="h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent motion-reduce:animate-none"
            />
            <span>{loadingLabel}</span>
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
